import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FMSLogger } from "FMSLogger";
import { LIVE_MISSIONS_ACTIONS } from "Utils/constants";
import { MISSION_DETAILS_MODAL, toggleModal } from "Slices/modals";
import { cancelLiveAndQueueMission } from "Slices/missionsPage";
//import { getVehiclesSyncStateById } from "Slices/vehiclesSyncState";
import {
  canOverrideMission,
  createMissionCancellationData,
  //createOverrideMissionWarningOptions,
  //findVehicleById,
  //isVehicleSyncing,
  OverrideMissionWarningOptions,
  resetOverrideMissionWarningOptions,
} from "./liveMissionsUtils";

const liveMissionsActionsLogger = FMSLogger.byPrefix(LIVE_MISSIONS_ACTIONS);

export type MissionAction = {
  name: string;
  callback: (item: any) => void;
  getIsDisabled?: (item: any) => boolean;
  dataTestId?: string;
};

/**
 * Custom hook for managing live missions actions
 * @param vehicles - Array of vehicles
 * @returns Object with mission actions and override warning state
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const useLiveMissionsActions = (vehicles: any[]) => {
  const dispatch = useDispatch();
  //const vehiclesSyncStateById = useSelector(getVehiclesSyncStateById);

  const [overrideMissionWarningOptions, setOverrideMissionWarningOptions] =
    useState<OverrideMissionWarningOptions>(
      resetOverrideMissionWarningOptions()
    );

  // Handle mission name click
  const handleMissionNameClick = useCallback(
    (missionId: string) => {
      dispatch(
        toggleModal({
          type: MISSION_DETAILS_MODAL,
          data: { missionId },
        })
      );
    },
    [dispatch]
  );

  // Handle mission cancellation
  const handleCancelMission = useCallback(
    (missionId: string, vehicleId: string) => {
      liveMissionsActionsLogger.debug(
        `Pressed cancel live mission with id:${missionId}`
      );

      const cancellationData = createMissionCancellationData(
        missionId,
        vehicleId
      );
      dispatch(cancelLiveAndQueueMission(cancellationData));
    },
    [dispatch]
  );

  // Handle mission override
  /*const handleOverrideMission = useCallback(
    (missionId: string, vehicleId: string) => {
      const vehicle = findVehicleById(vehicles, vehicleId);

      liveMissionsActionsLogger.debug(
        `Pressed override live mission with id:${missionId}`
      );

      const options = createOverrideMissionWarningOptions(missionId, vehicle);
      setOverrideMissionWarningOptions(options);
    },
    [vehicles]
  );*/

  // Handle close override warning
  const handleCloseOverrideWarning = useCallback(() => {
    setOverrideMissionWarningOptions(resetOverrideMissionWarningOptions());
  }, []);

  // Handle success override warning
  const handleSuccessOverrideWarning = useCallback(() => {
    if (!canOverrideMission(overrideMissionWarningOptions)) {
      return;
    }

    liveMissionsActionsLogger.debug(
      `handle plan mission for vehicle with override ${overrideMissionWarningOptions.vehicleName}`
    );

    // First cancel the current mission, then deploy a new one
    if (
      overrideMissionWarningOptions.missionId &&
      overrideMissionWarningOptions.vehicleId
    ) {
      // Cancel current mission
      dispatch(
        cancelLiveAndQueueMission({
          missionId: overrideMissionWarningOptions.missionId,
          vehicleId: overrideMissionWarningOptions.vehicleId,
        })
      );

      // TODO: Deploy new mission - this would need to be implemented based on business requirements
      // For now, we just close the warning
      liveMissionsActionsLogger.debug(
        "Mission override completed - current mission cancelled"
      );
    }

    setOverrideMissionWarningOptions(resetOverrideMissionWarningOptions());
  }, [overrideMissionWarningOptions, dispatch]);

  // Create mission actions
  const missionActions = useMemo(
    (): MissionAction[] => [
      {
        name: "Cancel",
        dataTestId: "cancel-mission-button",
        callback: (item: any) => handleCancelMission(item.id, item.vehicleId),
      },
      /*{
        name: "Override",
        dataTestId: "override-mission-button",
        getIsDisabled: (item: any) =>
          isVehicleSyncing(vehiclesSyncStateById, item.vehicleId),
        callback: (item: any) => handleOverrideMission(item.id, item.vehicleId),
      },*/
    ],
    [handleCancelMission /*, handleOverrideMission, vehiclesSyncStateById*/]
  );

  return {
    missionActions,
    overrideMissionWarningOptions,
    handleMissionNameClick,
    handleCloseOverrideWarning,
    handleSuccessOverrideWarning,
  };
};
